import React, { useEffect, useId, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  description: string;
  action: () => void;
  category?: string;
  disabled?: boolean;
  preventDefault?: boolean;
  stopPropagation?: boolean;
  modifiers?: {
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    meta?: boolean;
  };
}

export interface ShortcutContext {
  context: string;
  shortcuts: KeyboardShortcut[];
}

function isEditableElement(target: HTMLElement): boolean {
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.contentEditable === 'true'
  );
}

function isActivatableElement(target: HTMLElement): boolean {
  return (
    target.tagName === 'BUTTON' ||
    target.tagName === 'A' ||
    target.getAttribute?.('role') === 'button'
  );
}

function isActivationKey(key: string): boolean {
  return key === 'Enter' || key === ' ';
}

function isDomModalOpen(): boolean {
  return Boolean(
    document.querySelector('[role="dialog"][data-state="open"]') ||
    document.querySelector('.modal-open, [data-modal="true"]') ||
    document.querySelector('[data-radix-overlay]') ||
    document.querySelector('[data-radix-dialog-content]')
  );
}

// Global shortcut registry
class ShortcutRegistry {
  private contexts = new Map<string, KeyboardShortcut[]>();
  private activeContexts = new Set<string>();
  private listeners = new Set<() => void>();
  private globalEventListenerAttached = false;
  private modalContexts = new Set<string>(); // Track modal contexts

  // Define which contexts are mutually exclusive (page-level contexts)
  private pageContexts = new Set([
    'funds-page',
    'fund-detail',
    'fund-form',
    'investors-page',
    'dashboard-page',
  ]);

  register(context: string, shortcuts: KeyboardShortcut[]) {
    this.contexts.set(context, shortcuts);
    this.notifyListeners();
  }

  unregister(context: string) {
    this.contexts.delete(context);
    this.activeContexts.delete(context);
    this.notifyListeners();
  }

  activate(context: string) {
    // If this is a page context, deactivate other page contexts
    if (this.pageContexts.has(context)) {
      for (const pageContext of this.pageContexts) {
        if (pageContext !== context) {
          this.activeContexts.delete(pageContext);
        }
      }
    }

    this.activeContexts.add(context);
    this.ensureGlobalEventListener();
    this.notifyListeners();
  }

  private ensureGlobalEventListener() {
    if (!this.globalEventListenerAttached) {
      document.addEventListener('keydown', this.handleGlobalKeyDown);
      this.globalEventListenerAttached = true;
    }
  }

  private handleGlobalKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement;
    const isInput = isEditableElement(target);

    // Let open modals/dialogs handle Escape themselves
    if (
      event.key === 'Escape' &&
      (this.hasActiveModals() || isDomModalOpen())
    ) {
      return;
    }

    if (event.key === 'Escape' && isInput) {
      target.blur();
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Ignore other shortcuts when typing in inputs (unless specifically allowed with modifiers)
    if (isInput && !event.ctrlKey && !event.metaKey) {
      return;
    }

    // Enter/Space on a focused button or link is that element's own activation -
    // let the browser click it instead of also firing a registered shortcut.
    if (isActivationKey(event.key) && isActivatableElement(target)) {
      return;
    }

    // Skip all page-level shortcuts when a modal is active
    if (this.hasActiveModals()) {
      return;
    }

    const shortcut = this.findShortcut(event.key, event);
    if (!shortcut) {
      return;
    }

    if (shortcut.preventDefault !== false) {
      event.preventDefault();
    }
    if (shortcut.stopPropagation !== false) {
      event.stopPropagation();
    }

    shortcut.action();
  };

  deactivate(context: string) {
    this.activeContexts.delete(context);
    this.modalContexts.delete(context);
    this.notifyListeners();
  }

  // Methods to manage modal contexts
  activateModal(context: string) {
    this.modalContexts.add(context);
    this.activate(context);
  }

  deactivateModal(context: string) {
    this.modalContexts.delete(context);
    this.deactivate(context);
  }

  hasActiveModals(): boolean {
    return this.modalContexts.size > 0;
  }

  getActiveShortcuts(): ShortcutContext[] {
    const activeShortcuts: ShortcutContext[] = [];

    for (const context of this.activeContexts) {
      const shortcuts = this.contexts.get(context);
      if (shortcuts) {
        activeShortcuts.push({ context, shortcuts });
      }
    }

    return activeShortcuts;
  }

  findShortcut(key: string, modifiers: KeyboardEvent): KeyboardShortcut | null {
    // Page contexts take priority over non-page contexts (global, etc.)
    const active = Array.from(this.activeContexts);
    const orderedContexts = [
      ...active.filter((ctx) => this.pageContexts.has(ctx)),
      ...active.filter((ctx) => !this.pageContexts.has(ctx)),
    ];

    for (const context of orderedContexts) {
      const shortcut = this.contexts
        .get(context)
        ?.find((candidate) => this.matchesShortcut(candidate, key, modifiers));
      if (shortcut) {
        return shortcut;
      }
    }

    return null;
  }

  private matchesShortcut(
    shortcut: KeyboardShortcut,
    key: string,
    event: KeyboardEvent
  ): boolean {
    if (shortcut.disabled) return false;
    if (shortcut.key.toLowerCase() !== key.toLowerCase()) return false;
    return this.matchModifiers(shortcut.modifiers || {}, event);
  }

  private matchModifiers(
    required: NonNullable<KeyboardShortcut['modifiers']>,
    event: KeyboardEvent
  ): boolean {
    // If no modifiers are explicitly specified, only match when NO modifiers are pressed
    // This prevents single-key shortcuts from interfering with common Ctrl+key combinations
    const hasExplicitModifiers =
      required.ctrl !== undefined ||
      required.alt !== undefined ||
      required.shift !== undefined ||
      required.meta !== undefined;

    if (!hasExplicitModifiers) {
      // Only match if no modifiers are pressed (except shift for special keys like '?')
      return !event.ctrlKey && !event.altKey && !event.metaKey;
    }

    // If modifiers are explicitly specified, require exact match
    const ctrl = required.ctrl ?? false;
    const alt = required.alt ?? false;
    const shift = required.shift ?? false;
    const meta = required.meta ?? false;

    return (
      event.ctrlKey === ctrl &&
      event.altKey === alt &&
      event.shiftKey === shift &&
      event.metaKey === meta
    );
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener());
  }
}

// Global registry instance
const shortcutRegistry = new ShortcutRegistry();

// Hook for using keyboard shortcuts
export function useKeyboardShortcuts(
  context: string,
  shortcuts: KeyboardShortcut[],
  options: { enabled?: boolean } = {}
) {
  const { enabled = true } = options;
  const shortcutsRef = useRef(shortcuts);

  // Update shortcuts ref when they change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  // Register/unregister context
  useEffect(() => {
    if (!enabled) return;

    // Small delay to ensure previous page cleanup is complete
    const timeoutId = setTimeout(() => {
      shortcutRegistry.register(context, shortcuts);
      shortcutRegistry.activate(context);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      shortcutRegistry.deactivate(context);
      shortcutRegistry.unregister(context);
    };
  }, [context, shortcuts, enabled]);

  // Note: Event handling is now managed globally by the registry
}

// Hook for getting active shortcuts (for help overlay)
export function useActiveShortcuts() {
  const [shortcuts, setShortcuts] = React.useState<ShortcutContext[]>(() =>
    shortcutRegistry.getActiveShortcuts()
  );

  useEffect(() => {
    const updateShortcuts = () => {
      setShortcuts(shortcutRegistry.getActiveShortcuts());
    };
    const unsubscribe = shortcutRegistry.subscribe(updateShortcuts);

    return () => {
      unsubscribe();
    };
  }, []);

  return shortcuts;
}

// Hook for modal components to register themselves and prevent keyboard shortcuts
export function useModalKeyboardShortcuts(
  context: string,
  isOpen: boolean,
  shortcuts: KeyboardShortcut[] = []
) {
  useEffect(() => {
    if (!isOpen) return;

    // Register as modal context when open
    shortcutRegistry.activateModal(context);

    // If there are specific modal shortcuts, register them too
    if (shortcuts.length > 0) {
      shortcutRegistry.register(context, shortcuts);
    }

    return () => {
      shortcutRegistry.deactivateModal(context);
      if (shortcuts.length > 0) {
        shortcutRegistry.unregister(context);
      }
    };
  }, [context, isOpen, shortcuts]);
}

// Simple hook to prevent all keyboard shortcuts when a modal is open
export function useModalKeyboardPrevention(
  isOpen: boolean,
  modalName: string = 'modal'
) {
  // useId generates a stable unique ID that is pure and persists across renders
  const id = useId();
  useModalKeyboardShortcuts(`${modalName}-${id}`, isOpen, []);
}
